import { ReactNode, useMemo } from 'react';
import { Image } from 'react-datocms/image';
import { BlogPostCard, PaginatedItemsGrid } from '@carbonify/ui';
import { formatDate } from '@carbonify/ui/utils/format';
import clsx from 'clsx';
import BlockItem, { type Block } from 'components/Blocks';
import AuthorBlock from 'components/Blocks/AuthorBlock';
import InlineRichText from 'components/RichContent/InlineRichText';
import SearchForm from 'components/SearchForm/SearchForm';
import useMarketingUrl from 'hooks/useMarketingUrl';
import { usePaginatedQuery } from 'hooks/usePaginatedQuery';
import { useRouter } from 'next/router';
import querySearchBlogPosts from 'services/blog/querySearchBlogPosts';
import useConfig from 'services/config';

import styles from './GenericModule.module.css';

interface BlogSearchModuleRecord {
  id: string;
  anchor?: string;
  __typename: string;
  title: string;
  subtitle: string;
  theme: 'light' | 'dark';
  promotedCard?: Block;
  showSearch?: boolean;
  searchPlaceholder: string;
  nextPageLabel: string;
  previousPageLabel: string;
  loadMoreLabel: string;
}

const cardPlaceholders = Array(12)
  .fill(null)
  .map((_, key) => (
    <BlogPostCard
      key={key}
      image={null}
      title={null}
      loading
    />
  ));

export default function BlogSearchModule({
  title,
  subtitle,
  theme = 'light',
  showSearch = false,
  nextPageLabel,
  previousPageLabel,
  loadMoreLabel,
  promotedCard,
  searchPlaceholder,
  anchor,
}: BlogSearchModuleRecord) {
  const { locale, id } = useConfig();
  const getUrl = useMarketingUrl();
  const hasNoTitle = !title && !subtitle;
  const { query } = useRouter();

  const itemsPerPage = 12 - (promotedCard ? 1 : 0);
  const filter = useMemo(() => {
    const { category, search: pattern } = query;

    return {
      brand: { eq: id },
      redirectType: { notIn: ['302', '301'] },
      _locales: { anyIn: [locale] },
      ...(category ? { categories: { matches: { pattern: category } } } : {}),
      ...(pattern
        ? {
            OR: [
              { title: { matches: { pattern } } },
              { lead: { matches: { pattern } } },
              { blogPostContent: { matches: { pattern } } },
            ],
          }
        : {}),
    };
  }, [id, locale, query]);

  const { loading, currentPage, numberOfPages, items, goToPage } = usePaginatedQuery(
    querySearchBlogPosts,
    { filter, locale },
    { resultKey: 'allBlogPosts', itemsPerPage },
  );

  const searchResults = useMemo(
    () =>
      (Array.isArray(items) ? items : []).reduce(
        (results, { id, title, titleImage, slug, lead, author, _publishedAt }) => {
          results.push(
            <BlogPostCard
              key={id}
              href={getUrl(`blog/${slug}`)}
              title={title}
              description={lead && <InlineRichText as={null}>{lead}</InlineRichText>}
              image={
                titleImage && (
                  // eslint-disable-next-line jsx-a11y/alt-text
                  <Image
                    data={titleImage.responsiveImage}
                    objectFit="cover"
                    objectPosition="center"
                  />
                )
              }
              footer={
                author?.title && (
                  <AuthorBlock
                    {...author}
                    subtitle={_publishedAt && formatDate(_publishedAt, locale)}
                  />
                )
              }
            />,
          );

          if (promotedCard && results.length === 3) {
            results.push(<BlockItem {...promotedCard} />);
          }

          return results;
        },
        [] as ReactNode[],
      ),
    [items, locale, getUrl, promotedCard],
  );

  return (
    <section
      data-testid="blog-search"
      id={anchor}
      className={clsx(styles.module, styles[theme], hasNoTitle && styles.noTitle)}
    >
      {title && (
        <InlineRichText
          as="h2"
          className={styles.title}
        >
          {title}
        </InlineRichText>
      )}
      {subtitle && (
        <InlineRichText
          as="p"
          className={styles.subtitle}
        >
          {subtitle}
        </InlineRichText>
      )}
      {showSearch && <SearchForm searchPlaceholder={searchPlaceholder} />}
      <PaginatedItemsGrid
        items={loading ? cardPlaceholders : searchResults}
        currentPage={currentPage}
        numberOfPages={numberOfPages}
        onPageClick={goToPage}
        onNextClick={() => {
          goToPage(currentPage + 1);
        }}
        onPreviousClick={() => {
          goToPage(currentPage - 1);
        }}
        previousLabel={previousPageLabel}
        nextLabel={nextPageLabel}
        loadMoreLabel={loadMoreLabel}
        itemHeight="520px"
      />
    </section>
  );
}
